import { z } from 'zod';

// Also the order of rows with the same due date in the staff questionnaire list.
export const QUESTIONNAIRE_STATUS_KINDS = [
  'SF12',
  'BASELINE',
  'REOCCURING',
  'BAG',
  'BDM1',
  'BDM2',
] as const;
export type QuestionnaireStatusKind =
  (typeof QUESTIONNAIRE_STATUS_KINDS)[number];

// NOT_FILLED_OUT also covers "not detected": the Qualtrics fillout detection only
// runs in a time window per survey type. UNKNOWN = the stored state cannot be read.
export const QUESTIONNAIRE_FILLOUT_STATES = [
  'FILLED_OUT',
  'NOT_FILLED_OUT',
  'NOT_DUE_YET',
  'UNKNOWN',
] as const;
export type QuestionnaireFilloutState =
  (typeof QUESTIONNAIRE_FILLOUT_STATES)[number];

// Identifies a questionnaire for the on-demand fillout check. BDM1/BDM2 need no id:
// a participant has at most one link of each.
export const questionnaireCheckTargetDto = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('SURVEY_LINK'), surveyLinkId: z.number().int() }),
  z.object({
    kind: z.literal('BAG'),
    bagId: z.string(),
    horizon: z.number().int(),
  }),
  z.object({ kind: z.literal('BDM1') }),
  z.object({ kind: z.literal('BDM2') }),
]);
export type QuestionnaireCheckTargetDto = z.infer<
  typeof questionnaireCheckTargetDto
>;

export const questionnaireCheckTargetKey = (
  target: QuestionnaireCheckTargetDto,
): string => {
  switch (target.kind) {
    case 'SURVEY_LINK':
      return `${target.kind}:${target.surveyLinkId}`;
    case 'BAG':
      return `${target.kind}:${target.bagId}:${target.horizon}`;
    case 'BDM1':
    case 'BDM2':
      return target.kind;
  }
};

export const userQuestionnaireStatusDto = z.object({
  kind: z.enum(QUESTIONNAIRE_STATUS_KINDS),
  // as stored in the backend (REOCCURING and BAG only), not the staff "Horizon.Wave" label
  horizon: z.number().nullable(),
  wave: z.number().nullable(),
  // null = due before the acceptance (onboarding SF12)
  dueFrom: z.coerce.date().nullable(),
  state: z.enum(QUESTIONNAIRE_FILLOUT_STATES),
  filledOutAt: z.coerce.date().nullable(),
  link: z.string().nullable(),
  // null = no Qualtrics link to check (SF12, BDM without link)
  checkTarget: questionnaireCheckTargetDto.nullable(),
  // open for the on-demand fillout check
  checkable: z.boolean(),
});
export type UserQuestionnaireStatusDto = z.infer<
  typeof userQuestionnaireStatusDto
>;

export const userQuestionnaireStatusResDto = z.object({
  questionnaires: z.array(userQuestionnaireStatusDto),
});
export type UserQuestionnaireStatusResDto = z.infer<
  typeof userQuestionnaireStatusResDto
>;

export const QUESTIONNAIRE_CHECK_OUTCOMES = [
  'FILLED_OUT',
  // filled out, but detected after the detection window: no loyalty points, no voucher
  'FILLED_OUT_TOO_LATE',
  // filled out, the voucher choice is still open
  'VOUCHER_CHOICE_OPEN',
  // also when Qualtrics could not be reached
  'NOT_FILLED_OUT',
  // the questionnaire is not open for a check (any more)
  'NOT_CHECKABLE',
  'COOLDOWN',
] as const;
export type QuestionnaireCheckOutcome =
  (typeof QUESTIONNAIRE_CHECK_OUTCOMES)[number];

export const questionnaireCheckResDto = z.object({
  outcome: z.enum(QUESTIONNAIRE_CHECK_OUTCOMES),
});
export type QuestionnaireCheckResDto = z.infer<typeof questionnaireCheckResDto>;

export const QUESTIONNAIRE_CHECK_COOLDOWN_MS = 10_000;
